// ============================================================================
// CLOCHE « À TRAITER » (topbar) — format validé utilisateur 2026-08-05 : pastille de compteur
// alimentée par LA MÊME source que le triage du Dashboard (`triage.ts` — aucun état « lu/non-lu »,
// aucun doublon), déroulé listant les items avec leur destination. Le relevé est celui que partagent la
// cloche et les compteurs du menu (`triage-context`). §23.0 : une source en échec est DITE (pastille « ! »
// + pied), jamais un zéro.
//
// ⚠️ LE BOUTON EST CELUI DE LA BARRE (`TopbarAction`) et le panneau celui du dépôt (`Popover`), comme le lanceur
// voisin : un bouton écrit à la main gardait sa propre boîte, sans survol, et divergeait au premier ajustement. L'état
// « ouvert » se lit sur `aria-expanded`, que la primitive peint.
// ============================================================================
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CountBadge, Popover, TopbarAction } from '@orgazone/ui-web';
import { triageItems } from './triage';
import { useTriageCounts } from './triage-context';
import { TOPBAR_PANEL } from './topbar-panel';

export function TriageBell() {
  const counts = useTriageCounts();
  const [open, setOpen] = useState(false);

  const { items, failed } = counts ? triageItems(counts) : { items: [], failed: 0 };
  const total = items.reduce((a, t) => a + t.n, 0);
  const critical = items.some((t) => t.kind === 'da');

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      id="console-triage"
      label="À traiter"
      geom={TOPBAR_PANEL.geom(330)}
      style={TOPBAR_PANEL.style}
      trigger={(a) => (
        <TopbarAction
          icon="bell"
          label="À traiter"
          onClick={a.onClick}
          trigger={{ ref: a.ref, 'aria-expanded': a['aria-expanded'], 'aria-haspopup': a['aria-haspopup'], 'aria-controls': a['aria-controls'] }}
          // ⚠️ LE COMPTEUR DE LA BIBLIOTHÈQUE (`CountBadge`), pas une pastille écrite ici : la copie locale peignait son
          //    chiffre en blanc, illisible en thème sombre sur l'encre claire du ton.
          badge={(total > 0 || (failed > 0 && counts)) ? (
            <CountBadge overlay value={total > 0 ? total : '!'} tone={total > 0 ? (critical ? 'danger' : 'warn') : 'neutral'}
              srLabel={total > 0 ? `${total} à traiter` : 'relevé incomplet'} />
          ) : undefined}
        />
      )}
    >
      {({ close }) => (
        <>
          <div style={{ fontSize: 'var(--t-sub)', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ink500)', padding: '6px 10px 8px' }}>À traiter</div>
          {!counts && <div className="muted" style={{ padding: '4px 10px 10px', fontSize: 'var(--t-body)' }}>Relevé en cours…</div>}
          {counts && items.length === 0 && failed === 0 && (
            <div style={{ padding: '4px 10px 10px', fontSize: 'var(--t-body)', color: 'var(--ink600)' }}>Rien à traiter — tout est à jour. ✓</div>
          )}
          {items.map((t) => (
            <Link key={t.label} to={t.to} onClick={close}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 'var(--r-sm)', textDecoration: 'none', color: 'inherit' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--ink100)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
            >
              <span style={{ minWidth: 24, height: 24, borderRadius: 'var(--r-xs)', display: 'grid', placeItems: 'center', fontSize: 'var(--t-meta)', fontWeight: 700, background: `var(--${t.kind}-bg)`, color: `var(--${t.kind}-fg)` }}>{t.n}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 600 }}>{t.label}</span>
                <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink500)', lineHeight: 1.35 }}>{t.detail}</span>
              </span>
            </Link>
          ))}
          {counts && failed > 0 && (
            <div style={{ padding: '8px 10px 4px', fontSize: 'var(--t-meta)', color: 'var(--wa-fg)', borderTop: items.length ? '1px solid var(--ink100)' : 'none', marginTop: items.length ? 6 : 0 }}>
              ⚠️ {failed} source{failed > 1 ? 's' : ''} indisponible{failed > 1 ? 's' : ''} — la liste peut être incomplète.
            </div>
          )}
        </>
      )}
    </Popover>
  );
}
